/**
 * Environment detection utility
 * Extracts environment and region from the current URL to construct API gateway URLs
 */

export interface EnvironmentConfig {
  environment: string;
  region: string;
}

/**
 * Extracts environment and region from the current URL
 * 
 * URL Pattern: https://{environment}.{region}.command-center.rnr.amazon.dev/...
 * Examples:
 * - https://beta.us-east-1.command-center.rnr.amazon.dev/ → { environment: 'beta', region: 'us-east-1' }
 * - https://gamma.us-west-2.command-center.rnr.amazon.dev/ → { environment: 'gamma', region: 'us-west-2' }
 * - localhost:3000 → { environment: 'beta', region: 'us-east-1' } (defaults)
 */
export function detectEnvironment(): EnvironmentConfig {
  const defaultConfig: EnvironmentConfig = {
    environment: 'gamma',
    region: 'us-east-1'
  };

  try {
    const hostname = window.location.hostname;

    // Handle local development environments (including dev-dsk cloud desktops)
    if (hostname === 'localhost' || hostname === '0.0.0.0' || hostname.startsWith('192.168.') || hostname.endsWith('.local') || hostname.startsWith('dev-dsk-')) {
      console.log('[Environment Detection] Local environment detected, using defaults:', defaultConfig);
      return defaultConfig;
    }

    // Parse production-like URLs
    // Expected pattern: {environment}.{region}.command-center.rnr.amazon.dev
    const parts = hostname.split('.');
    
    if (parts.length >= 2) {
      const environment = parts[0]; // First part is the environment (beta, gamma, prod, etc.)
      const region = parts[1]; // Second part is the region (us-east-1, us-west-2, etc.)
      
      // Validate that we have reasonable values
      if (environment && region) {
        const config: EnvironmentConfig = {
          environment,
          region
        };
        
        console.log('[Environment Detection] Detected environment from URL:', config);
        return config;
      }
    }

    console.log('[Environment Detection] Could not parse URL, using defaults:', defaultConfig);
    return defaultConfig;

  } catch (error) {
    console.warn('[Environment Detection] Error detecting environment, using defaults:', error);
    return defaultConfig;
  }
}

/**
 * Constructs the API gateway URL based on the current environment
 * 
 * @returns The complete API gateway URL
 */
export function getApiGatewayUrl(): string {
  const prefix = process.env.API_GATEWAY_PREFIX || 'https://api.';
  const suffix = process.env.API_GATEWAY_SUFFIX || '.rcc-gateway.returns.amazon.dev';
  
  const { environment, region } = detectEnvironment();
  
  // Construct URL: {prefix}{environment}.{region}{suffix}
  const apiUrl = `${prefix}${environment}.${region}${suffix}`;
  
  console.log('[Environment Detection] Constructed API Gateway URL:', apiUrl);
  return apiUrl;
}

/**
 * Checks if the application is running in a local development environment.
 * Matches localhost, 0.0.0.0, 192.168.x LAN addresses, .local domains,
 * and dev-dsk-* cloud desktop hostnames.
 *
 * @returns true if running locally or on a cloud desktop, false otherwise
 */
export function isLocalEnvironment(): boolean {
  try {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '0.0.0.0' || hostname.startsWith('192.168.') || hostname.endsWith('.local') || hostname.startsWith('dev-dsk-');
  } catch (error) {
    console.warn('[Environment Detection] Error checking local environment:', error);
    return false;
  }
}
